import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowUpRight, Play } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { NuyCoinSvg } from '../common/NuyIcon';

const useHeroSlides = () => useQuery({ queryKey: ['hero-slides'], queryFn: () => api.get('/hero-slides').then((r) => r.data), staleTime: 60000 });

function SlideMedia({ s }) {
  if (s.type === 'video' && s.media_url) {
    return (
      <video className="gx-hero-media" src={s.media_url} poster={s.poster_url || undefined} autoPlay muted loop playsInline preload="metadata" />
    );
  }
  if (s.type === 'image' && s.media_url) {
    return <img className="gx-hero-media" src={s.media_url} alt={s.title} loading="eager" />;
  }
  const accent = s.accent || '#00E5FF';
  return (
    <div className="gx-hero-brand" style={{ '--accent': accent }}>
      {s.media_url && <img src={s.media_url} alt="" className="gx-hero-brand-bg" />}
      <div className="gx-hero-brand-glow" />
      <div className="gx-hero-brand-grid" />
      <div className="gx-hero-brand-inner">
        <div className="flex items-center justify-between gap-2">
          <span className="gx-hero-brand-mark"><NuyCoinSvg className="w-6 h-6" id={`hero-${s.id}`} /><span>NUYED</span></span>
          {s.badge && <span className="gx-hero-brand-tag">{s.badge}</span>}
        </div>
        <div className="gx-hero-brand-copy">
          <h3 className="gx-hero-brand-title">{s.title}</h3>
          {s.subtitle && <p className="gx-hero-brand-sub">{s.subtitle}</p>}
          {s.cta_label && <span className="gx-hero-brand-cta">{s.cta_label} <ArrowUpRight className="w-3.5 h-3.5" /></span>}
        </div>
      </div>
    </div>
  );
}

function Slide({ s, index }) {
  const isExternal = /^https?:\/\//.test(s.cta_url || '');
  const brand = s.type === 'brand';
  const inner = (
    <>
      <div className="gx-hero-card-media">
        <SlideMedia s={s} />
        {s.badge && !brand && <span className="gx-hero-badge" style={{ '--accent': s.accent || '#00E5FF' }}>{s.badge}</span>}
        {s.type === 'video' && <span className="gx-hero-play"><Play className="w-3 h-3" /></span>}
      </div>
      <div className="gx-hero-card-text">
        <p className="gx-hero-card-title">{brand ? (s.badge || 'NUYED') : s.title}</p>
        <div className="flex items-start justify-between gap-3">
          <p className="gx-hero-card-sub">{brand ? (s.cta_label || 'Learn more') : (s.subtitle || '')}</p>
          {s.cta_url && <ArrowUpRight className="w-4 h-4 text-gx-muted flex-shrink-0 mt-0.5 transition group-hover:text-gx-cyan group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
        </div>
      </div>
    </>
  );
  const cls = 'gx-hero-card group';
  const style = { animationDelay: `${index * 70}ms` };
  if (!s.cta_url) return <div className={cls} style={style} data-testid={`hero-slide-${index}`}>{inner}</div>;
  if (isExternal) return <a href={s.cta_url} target="_blank" rel="noreferrer" className={cls} style={style} data-testid={`hero-slide-${index}`}>{inner}</a>;
  return <Link to={s.cta_url} className={cls} style={style} data-testid={`hero-slide-${index}`}>{inner}</Link>;
}

export default function HeroSlider({ intervalMs = 6000 }) {
  const { data } = useHeroSlides();
  const items = data?.items || [];
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = items.length;

  const scrollTo = useCallback((i) => {
    const el = ref.current;
    if (!el || !count) return;
    const n = ((i % count) + count) % count;
    const card = el.children[n];
    if (card) el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: 'smooth' });
    setIdx(n);
  }, [count]);

  useEffect(() => {
    if (paused || count < 2) return;
    const t = setInterval(() => {
      const el = ref.current;
      if (!el) return;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
      scrollTo(atEnd ? 0 : idx + 1);
    }, intervalMs);
    return () => clearInterval(t);
  }, [idx, paused, count, intervalMs, scrollTo]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      let best = 0, bd = Infinity;
      Array.from(el.children).forEach((c, i) => { const d = Math.abs(c.offsetLeft - el.offsetLeft - el.scrollLeft); if (d < bd) { bd = d; best = i; } });
      setIdx(best);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [count]);

  if (!count) {
    return <div className="gx-hero-skeleton" data-testid="hero-skeleton"><span /><span /><span /></div>;
  }

  return (
    <section className="gx-hero" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="hero-section">
      <div ref={ref} className="gx-hero-track" data-testid="hero-track">
        {items.map((s, i) => <Slide key={s.id || i} s={s} index={i} />)}
      </div>
      {count > 1 && (
        <div className="gx-hero-controls">
          <div className="gx-hero-dots" data-testid="hero-dots">
            {items.map((_, i) => <button key={i} onClick={() => scrollTo(i)} className={`gx-hero-dot ${i === idx ? 'is-active' : ''}`} aria-label={`Slide ${i + 1}`} data-testid={`hero-dot-${i}`} />)}
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => scrollTo(idx - 1)} className="gx-hero-arrow" aria-label="Previous" data-testid="hero-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => scrollTo(idx + 1)} className="gx-hero-arrow" aria-label="Next" data-testid="hero-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </section>
  );
}
